/**
 * Desktop search integration. Submenu panels are intentionally inert until
 * their follow-up implementation.
 */
(function() {
  const sectionSelector = '[data-section-type="site-header"]';

  function getSearchApp() {
    const existing = window.theme && window.theme.SearchApp;
    if (existing && typeof existing.showModal === 'function') {
      return existing;
    }

    // Predictive Search custom element
    const predictive = document.querySelector('predictive-search.search');
    if (predictive && typeof predictive.showModal === 'function') {
      window.theme = window.theme || {};
      window.theme.SearchApp = predictive;
      return predictive;
    }

    // Tagalys Vue instance mounted on #search--app
    const tagalysEl = document.querySelector('#search--app');
    const tagalysApp = tagalysEl && tagalysEl.__vue__;
    if (tagalysApp && typeof tagalysApp.showModal === 'function') {
      window.theme = window.theme || {};
      window.theme.SearchApp = tagalysApp;
      return tagalysApp;
    }

    return null;
  }

  function getSearchPanelElement() {
    const searchApp = getSearchApp();
    if (searchApp) {
      if (searchApp.$el) {
        return searchApp.$el;
      }
      if (searchApp.nodeType === 1) {
        return searchApp;
      }
    }
    return (
      document.querySelector('#search--app') ||
      document.querySelector('predictive-search.search')
    );
  }

  function isDesktop() {
    return window.matchMedia &&
      window.matchMedia('(min-width: 990px)').matches;
  }

  function isSearchOpen() {
    const searchApp = getSearchApp();
    return (searchApp && searchApp.isVisible) ||
      document.body.classList.contains('search-is-visible');
  }

  function getDesktopSearch(section) {
    return section.querySelector('.shd__search[data-shd-search]');
  }

  function resetDesktopSearchUi(section) {
    const search = getDesktopSearch(section);
    if (!search) {
      return;
    }
    search.classList.remove('is-focused');
    const input = search.querySelector('[data-shd-search-input]');
    if (input && document.activeElement === input) {
      input.blur();
    }
  }

  function syncSearchUiState(section, isOpen) {
    const search = getDesktopSearch(section);
    if (!search) {
      return;
    }
    const input = search.querySelector('[data-shd-search-input]');
    const closeTrigger = search.querySelector('[data-shd-search-close]');

    search.classList.toggle('is-open', isOpen);
    if (isOpen) {
      search.classList.add('is-focused');
    }
    if (input) {
      input.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
    if (closeTrigger) {
      closeTrigger.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      closeTrigger.tabIndex = isOpen ? 0 : -1;
    }
  }

  function isDesktopSearchTarget(node) {
    return node && node.closest &&
      node.closest('[data-shd-search], #shopify-search-host, #search--app, predictive-search.search, .tagalys-search-suggestions, .search-suggestions');
  }

  function activateSearch(section, search, input) {
    if (section.__shdIgnoreSearchFocus || section.__shdClosingSearch) {
      return;
    }
    search.classList.add('is-focused');
    if (!isSearchOpen()) {
      openSearch(section, input);
      return;
    }
    syncSearchUiState(section, true);
    positionSearch(section);
    syncQueryToSearchApp(input);
  }

  // Tagalys showModal focuses the panel input and adds overlay dismiss listeners —
  // both break the header pill on desktop. Patch once after SearchApp mounts.
  function patchSearchAppForDesktop() {
    const app = getSearchApp();
    if (!app || app.__shdDesktopPatched) {
      return;
    }
    app.__shdDesktopPatched = true;

    if (app.boundClickHandler) {
      if (app.mainContent) {
        app.mainContent.removeEventListener('click', app.boundClickHandler);
      }
      if (app.bgOverlay) {
        app.bgOverlay.removeEventListener('click', app.boundClickHandler);
      }
    }

    const nativeShow = app.showModal.bind(app);
    const nativeHide = app.hideModal.bind(app);
    const panel = app.$el || app;

    app.showModal = function() {
      if (!isDesktop()) {
        nativeShow();
        return;
      }
      window.theme = window.theme || {};
      window.theme.lastActiveElement = null;
      document.body.classList.add('search-is-visible');
      app.isVisible = true;
      if (app.bgOverlay) {
        app.bgOverlay.setAttribute('aria-hidden', 'false');
      }
      if (panel.setAttribute) {
        panel.setAttribute('aria-hidden', 'false');
      }
      if (panel.classList) {
        panel.classList.add('is-visible');
      }
      if (!app.wasModalShown) {
        if (typeof app.setTrendingSearches === 'function') {
          app.setTrendingSearches();
        }
        app.wasModalShown = true;
      }
    };

    app.hideModal = function() {
      if (!isDesktop()) {
        nativeHide();
        return;
      }
      window.theme = window.theme || {};
      window.theme.lastActiveElement = null;
      document.body.classList.remove('search-is-visible');
      app.isVisible = false;
      if (app.bgOverlay) {
        app.bgOverlay.setAttribute('aria-hidden', 'true');
      }
      if (panel.setAttribute) {
        panel.setAttribute('aria-hidden', 'true');
      }
      if (panel.classList) {
        panel.classList.remove('is-visible');
      }
      document.querySelectorAll(sectionSelector).forEach(function(sec) {
        syncSearchUiState(sec, false);
        resetDesktopSearchUi(sec);
      });
    };
  }

  function syncDesktopSearchState(search) {
    const input = search && search.querySelector('[data-shd-search-input]');
    if (!input) {
      return;
    }
    search.classList.toggle('has-value', !!(input.value && input.value.trim()));
  }

  function positionSearch(section) {
    const searchElement = getSearchPanelElement();
    const headerBar = section.querySelector('.shd__bar');

    if (searchElement && searchElement.style && headerBar) {
      // Measure AFTER the modal opens: `search-is-visible` sets
      // #shopify-section-announcement { display:none }, so the header rises and
      // its bottom becomes the true, flush offset for the modal. Measuring
      // before opening captured the announcement height as an extra gap.
      const bottom = headerBar.getBoundingClientRect().bottom;
      searchElement.style.top = Math.max(Math.round(bottom), 0) + 'px';
    }
  }

  function getSearchAppInput(app) {
    if (!app) {
      return null;
    }
    if (app.searchInput) {
      return app.searchInput;
    }
    if (app.$refs && app.$refs.searchQuery) {
      return app.$refs.searchQuery;
    }
    const panel = getSearchPanelElement();
    if (panel && panel.querySelector) {
      return (
        panel.querySelector('#search-input-main') ||
        panel.querySelector('input[name="q"]') ||
        panel.querySelector('input[name="query"]')
      );
    }
    return (
      document.querySelector('#search-input-main') ||
      document.querySelector('#search--app input[name="q"]')
    );
  }

  function syncQueryToSearchApp(headerInput) {
    const app = getSearchApp();
    const appInput = getSearchAppInput(app);
    if (!appInput || !headerInput) {
      return;
    }

    const nextValue = headerInput.value;
    if (appInput.value !== nextValue) {
      appInput.value = nextValue;
    }

    try {
      // Tagalys listens for native input events on #search-input-main.
      appInput.dispatchEvent(new Event('input', { bubbles: true }));
      appInput.dispatchEvent(new Event('keyup', { bubbles: true }));
    } catch (err) {
      // IE-safe no-op; modern storefronts support Event constructor.
    }

    // Predictive Search custom element path.
    if (typeof app.onSearch === 'function') {
      app.onSearch();
    }
  }

  function scheduleSearchOpen(section, headerInput) {
    if (section.__shdSearchRetryTimer) {
      return;
    }
    let attempts = 0;
    function retry() {
      section.__shdSearchRetryTimer = null;
      if (section.__shdClosingSearch || section.__shdIgnoreSearchFocus) {
        return;
      }
      if (getSearchApp()) {
        openSearch(section, headerInput);
        return;
      }
      if (++attempts < 20) {
        section.__shdSearchRetryTimer = window.setTimeout(retry, 50);
      }
    }
    section.__shdSearchRetryTimer = window.setTimeout(retry, 0);
  }

  function closeSearch(section) {
    if (section.__shdClosingSearch) {
      return;
    }
    section.__shdClosingSearch = true;
    section.__shdIgnoreSearchFocus = true;
    if (section.__shdSearchRetryTimer) {
      window.clearTimeout(section.__shdSearchRetryTimer);
      section.__shdSearchRetryTimer = null;
    }

    window.theme = window.theme || {};
    window.theme.lastActiveElement = null;

    patchSearchAppForDesktop();
    const searchApp = getSearchApp();
    if (searchApp && typeof searchApp.hideModal === 'function') {
      if (searchApp.isVisible || document.body.classList.contains('search-is-visible')) {
        searchApp.hideModal();
      }
    } else {
      document.body.classList.remove('search-is-visible');
      syncSearchUiState(section, false);
      resetDesktopSearchUi(section);
    }

    window.setTimeout(function() {
      section.__shdClosingSearch = false;
      section.__shdIgnoreSearchFocus = false;
    }, 150);
  }

  function openSearch(section, headerInput) {
    if (section.__shdClosingSearch || section.__shdIgnoreSearchFocus) {
      return;
    }

    patchSearchAppForDesktop();

    const searchApp = getSearchApp();
    const search = getDesktopSearch(section);
    const input = headerInput || (search && search.querySelector('[data-shd-search-input]'));

    if (!searchApp) {
      scheduleSearchOpen(section, input);
      return;
    }

    if (isSearchOpen()) {
      syncSearchUiState(section, true);
      positionSearch(section);
      syncQueryToSearchApp(input);
      return;
    }

    if (typeof searchApp.showModal === 'function') {
      searchApp.showModal();
    } else {
      return;
    }

    syncSearchUiState(section, true);
    requestAnimationFrame(function() {
      positionSearch(section);
      syncQueryToSearchApp(input);
    });
  }

  function getActivePlaceholderQuery(search) {
    if (!search || !search.querySelector) {
      return '';
    }
    const active = search.querySelector('[data-ph-word].is-active');
    const fallback = active || search.querySelector('[data-ph-word]');
    if (!fallback) {
      return '';
    }
    return String(fallback.getAttribute('data-label') || fallback.textContent || '').trim();
  }

  function getProviderSearchParam() {
    return window.theme && window.theme.searchProvider === 'tagalys' ? 'query' : 'q';
  }

  function rememberSearchQuery(query) {
    if (!query) {
      return;
    }
    let lastSearches = [];
    try {
      lastSearches = localStorage.getItem('lastSearches')
        ? JSON.parse(localStorage.getItem('lastSearches'))
        : [];
    } catch (err) {
      lastSearches = [];
    }
    lastSearches = lastSearches.filter(function(s) {
      return String(s).toLowerCase() !== query.toLowerCase();
    });
    lastSearches.unshift(query);
    lastSearches = lastSearches.slice(0, 10);
    try {
      localStorage.setItem('lastSearches', JSON.stringify(lastSearches));
    } catch (err) {
      // ignore
    }
  }

  // Self-contained cart count. The theme's global side-cart updater
  // (side-cart.js onCartUpdated) writes the item count into EVERY
  // `.header__cart-count` element — our badge carries that class — so we never
  // read the legacy header's DOM. We only re-derive the empty/aria state from
  // the badge's own value whenever the cart changes.
  function refreshBag(section) {
    // Device-neutral: the badge always carries `.header__cart-count`
    // (desktop `.shd__bag-count` / mobile `.shm__bag-count` both add it).
    const target = section.querySelector('.header__cart-count');
    if (!target) {
      return;
    }

    const count = parseInt(target.textContent, 10) || 0;
    target.classList.toggle('hide', count === 0);

    const bagLink = target.closest('a');
    if (bagLink && bagLink.getAttribute('data-bag-label')) {
      bagLink.setAttribute('aria-label', bagLink.getAttribute('data-bag-label') + ' (' + count + ')');
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Rotating, linkable search placeholder (desktop pill only). The header input
  // opens the SearchApp panel; a sibling icon <a> ([data-shd-search-go]) navigates
  // to whichever word is currently shown. Scoped per pill so mobile inline
  // search words are not mixed into this rotator.
  function setupPlaceholder(section) {
    const pill = getDesktopSearch(section);
    if (!pill) {
      return;
    }
    const goLink = pill.querySelector('[data-shd-search-go]');
    const input = pill.querySelector('[data-shd-search-input]');
    const words = pill.querySelectorAll('[data-ph-word]');
    if (!goLink || words.length === 0) {
      return;
    }

    const openSearchLabel = (goLink.getAttribute('aria-label')) || 'Search';
    const searchUrl = goLink.getAttribute('data-search-url') || '/search';

    function syncGo(word) {
      const link = word && word.getAttribute('data-link');
      const label = (word && word.getAttribute('data-label')) || '';
      if (link) {
        goLink.setAttribute('href', link);
        goLink.removeAttribute('data-no-link');
        goLink.setAttribute('aria-label', label ? 'Go to ' + label : openSearchLabel);
      } else {
        goLink.setAttribute('href', searchUrl);
        goLink.setAttribute('data-no-link', '');
        goLink.setAttribute('aria-label', openSearchLabel);
      }
    }

    // Start from whichever word the markup flagged active.
    let index = 0;
    for (let i = 0; i < words.length; i++) {
      if (words[i].classList.contains('is-active')) {
        index = i;
        break;
      }
    }
    syncGo(words[index]);

    // Icon click: no link -> open search (avoid focus-before-click layout shift).
    goLink.addEventListener('click', function(event) {
      if (!goLink.hasAttribute('data-no-link')) {
        return;
      }
      event.preventDefault();
      if (input) {
        if (!isSearchOpen()) {
          openSearch(section, input);
        }
        input.focus({ preventScroll: true });
      }
    });

    // Nothing to rotate: a single word, or the user opted out of motion.
    if (words.length < 2 || prefersReducedMotion()) {
      return;
    }

    let hovering = false;
    pill.addEventListener('mouseenter', function() { hovering = true; });
    pill.addEventListener('mouseleave', function() { hovering = false; });
    pill.addEventListener('focusin', function() { hovering = true; });
    pill.addEventListener('focusout', function() { hovering = false; });

    // Pause while the user is on the pill, while the modal is open, or while the
    // tab is hidden — the interval keeps ticking but skips the swap.
    function paused() {
      return hovering ||
        document.hidden ||
        pill.classList.contains('is-focused') ||
        pill.classList.contains('has-value') ||
        document.body.classList.contains('search-is-visible');
    }

	function getDesktopPhInterval(section) {
		const raw = section && section.getAttribute('data-ph-interval');
		const value = parseInt(raw, 10);
		return value >= 2000 ? value : 4000;
	}

    function advance() {
      if (paused()) {
        return;
      }
      const current = words[index];
      const nextIndex = (index + 1) % words.length;
      const next = words[nextIndex];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      // Once it has slid out (opacity 0 throughout), park it back below the box
      // so it's ready to rise again next cycle.
      window.setTimeout(function() {
        current.classList.remove('is-leaving');
      }, 550);

      next.classList.remove('is-leaving');
      next.classList.add('is-active');

      index = nextIndex;
      syncGo(next);
    }

	section.__shdPhTimer = window.setInterval(
      advance,
      getDesktopPhInterval(section)
    );
  }

  function setupDesktopSearch(section) {
    const search = getDesktopSearch(section);
    if (!search || search.__shdSearchBound) {
      return;
    }
    search.__shdSearchBound = true;
    patchSearchAppForDesktop();

    const input = search.querySelector('[data-shd-search-input]');
    const form = search.querySelector('[data-shd-search-form]');
    const closeBtn = search.querySelector('[data-shd-search-close]');

    if (input) {
      // Open on click (mouse), not focus — focus runs before click finishes and
      // the announcement bar hiding shifts the header so the click hits the overlay.
      input.addEventListener('pointerdown', function() {
        section.__shdPointerOnInput = true;
      });
      input.addEventListener('focus', function() {
        if (!section.__shdPointerOnInput) {
          activateSearch(section, search, input);
        }
      });
      input.addEventListener('click', function() {
        section.__shdPointerOnInput = false;
        activateSearch(section, search, input);
      });
      input.addEventListener('input', function() {
        if (section.__shdIgnoreSearchFocus || section.__shdClosingSearch) {
          return;
        }
        syncDesktopSearchState(search);
        if (!isSearchOpen()) {
          openSearch(section, input);
        } else {
          syncQueryToSearchApp(input);
        }
      });
    }

    if (form) {
      form.addEventListener('submit', function(event) {
        let query = input ? input.value.trim() : '';
        if (!query && input) {
          query = getActivePlaceholderQuery(search);
          if (query) {
            input.value = query;
            syncDesktopSearchState(search);
            syncQueryToSearchApp(input);
          }
        }

        if (!query) {
          event.preventDefault();
          return;
        }

        rememberSearchQuery(query);

        const desiredParam = getProviderSearchParam();
        if (input && input.name !== desiredParam) {
          event.preventDefault();
          window.location.assign(
            (form.getAttribute('action') || '/search') +
            '?' + desiredParam + '=' + encodeURIComponent(query)
          );
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('mousedown', function(event) {
        event.preventDefault();
      });
      closeBtn.addEventListener('click', function(event) {
        event.preventDefault();
        closeSearch(section);
      });
    }

    const onPointerDownOutside = function(event) {
      if (!isDesktop() || !isSearchOpen()) {
        return;
      }
      if (isDesktopSearchTarget(event.target)) {
        return;
      }
      closeSearch(section);
    };
    document.addEventListener('pointerdown', onPointerDownOutside, true);
    section.__shdSearchOutsideHandler = onPointerDownOutside;

    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && isSearchOpen()) {
        closeSearch(section);
      }
    });

    syncDesktopSearchState(search);
    syncSearchUiState(section, isSearchOpen());
  }

  // Desktop sub-menu (mega) panels. Hovering or focusing a top-level item that
  // has children opens its full-width panel and dims the rest of the page with
  // the shared grey overlay (the same layer the search modal uses). Positioning
  // is pure CSS (the panel is absolute under the bar); JS only toggles state.
  function setupSubmenus(section) {
    const items = section.querySelectorAll('.shd__item[data-has-children="true"]');
    if (!items.length) {
      return;
    }

    const overlay = document.querySelector('.bg-overlay');
    let openItem = null;
    let closeTimer = null;

    function cancelClose() {
      if (!closeTimer) {
        return;
      }
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    function setExpanded(item, expanded) {
      const link = item.querySelector('.shd__link');
      if (link) {
        link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
    }

    function openPanel(item) {
      cancelClose();
      if (openItem === item) {
        return;
      }
      const switching = openItem && openItem !== item;
      if (openItem) {
        openItem.classList.remove('is-open');
        setExpanded(openItem, false);
      }
      if (switching) {
        document.body.classList.add('shd-submenu-switching');
      }
      openItem = item;
      item.classList.add('is-open');
      setExpanded(item, true);
      document.body.classList.add('shd-submenu-open');
      if (switching) {
        window.requestAnimationFrame(function() {
          document.body.classList.remove('shd-submenu-switching');
        });
      }
    }

    function closePanel() {
      cancelClose();
      if (!openItem) {
        return;
      }
      openItem.classList.remove('is-open');
      setExpanded(openItem, false);
      openItem = null;
      document.body.classList.remove('shd-submenu-open', 'shd-submenu-switching');
    }

    function scheduleClose() {
      cancelClose();
      // Small grace period so moving between adjacent items doesn't flicker.
      closeTimer = window.setTimeout(closePanel, 120);
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      item.addEventListener('mouseenter', function() {
        openPanel(item);
      });
      item.addEventListener('mouseleave', scheduleClose);
      item.addEventListener('focusin', function() {
        openPanel(item);
      });
      item.addEventListener('focusout', function(event) {
        if (item.contains(event.relatedTarget)) {
          return;
        }
        scheduleClose();
      });
    }

    const onOverlayClick = function() {
      closePanel();
    };
    const onKeydown = function(event) {
      if (event.key === 'Escape') {
        closePanel();
      }
    };
    const onScroll = function() {
      closePanel();
    };

    if (overlay) {
      overlay.addEventListener('click', onOverlayClick);
    }
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('scroll', onScroll, {passive: true});

    section.__shdSubmenuCleanup = function() {
      closePanel();
      if (overlay) {
        overlay.removeEventListener('click', onOverlayClick);
      }
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('scroll', onScroll);
    };
  }

  // Desktop bar fitting. The search shrinks first via CSS flex; this adds the
  // wrap class ONLY when the search has bottomed out at its min-width and the
  // menu still overflows one row — then the menu (not the search) wraps to a
  // second row, so the search is never pushed off the right edge.
  function fitHeader(section) {
    const bar = section.querySelector('.shd__bar');
    const menu = section.querySelector('.shd__menu');
    if (!bar || !menu) {
      return;
    }
    // Reset to one row, then measure. Reading scrollWidth forces a layout, so
    // the search has already flex-shrunk to its min before we compare widths.
    menu.classList.remove('shd__menu--wrap');
    if (bar.scrollWidth - bar.clientWidth > 1) {
      menu.classList.add('shd__menu--wrap');
    }
  }

  let fitQueued = false;
  function fitAllHeaders() {
    fitQueued = false;
    const nodes = document.querySelectorAll(sectionSelector);
    for (let i = 0; i < nodes.length; i++) {
      fitHeader(nodes[i]);
    }
    document.dispatchEvent(new CustomEvent('header:metrics-updated'));
  }
  function queueFit() {
    if (fitQueued) {
      return;
    }
    fitQueued = true;
    window.requestAnimationFrame(fitAllHeaders);
  }

  function init(section) {
    if (!section || section.__shdInit) {
      return;
    }
    section.__shdInit = true;

    setupDesktopSearch(section);
    setupPlaceholder(section);
    setupSubmenus(section);
    fitHeader(section);

    if (section.querySelector('.shd__bag-count')) {
      refreshBag(section);
      const onCartChange = function() {
        refreshBag(section);
      };
      // Broadcast by the theme on every cart change (cart.js / side-cart.js).
      document.addEventListener('cart:rendered', onCartChange);
      document.addEventListener('side-cart:rendered', onCartChange);
      section.__shdCartHandler = onCartChange;
    }
  }

  function notifyHeaderMetrics() {
    document.dispatchEvent(new CustomEvent('header:metrics-updated'));
  }

  function boot() {
    const nodes = document.querySelectorAll(sectionSelector);
    for (let i = 0; i < nodes.length; i++) {
      init(nodes[i]);
    }
    notifyHeaderMetrics();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function(event) {
    if (!event.target || !event.target.querySelector) {
      return;
    }
    const node = event.target.matches && event.target.matches(sectionSelector)
      ? event.target
      : event.target.querySelector(sectionSelector);
    init(node);
    notifyHeaderMetrics();
  });

  document.addEventListener('shopify:section:unload', function(event) {
    if (!event.target || !event.target.querySelector) {
      return;
    }
    const node = event.target.matches && event.target.matches(sectionSelector)
      ? event.target
      : event.target.querySelector(sectionSelector);
    if (node && node.__shdCartHandler) {
      document.removeEventListener('cart:rendered', node.__shdCartHandler);
      document.removeEventListener('side-cart:rendered', node.__shdCartHandler);
    }
    if (node && node.__shdPhTimer) {
      window.clearInterval(node.__shdPhTimer);
      node.__shdPhTimer = null;
    }
    if (node && node.__shdSubmenuCleanup) {
      node.__shdSubmenuCleanup();
      node.__shdSubmenuCleanup = null;
    }
    if (node && node.__shdSearchOutsideHandler) {
      document.removeEventListener('pointerdown', node.__shdSearchOutsideHandler, true);
      node.__shdSearchOutsideHandler = null;
    }
    if (node && node.__shdSearchRetryTimer) {
      window.clearTimeout(node.__shdSearchRetryTimer);
      node.__shdSearchRetryTimer = null;
    }
  });

  // Re-fit the bar (search shrink, then menu wrap) on resize, and keep the open
  // search modal flush with the header.
  window.addEventListener('resize', function() {
    queueFit();
    if (!document.body.classList.contains('search-is-visible')) {
      return;
    }
    const nodes = document.querySelectorAll(sectionSelector);
    for (let i = 0; i < nodes.length; i++) {
      positionSearch(nodes[i]);
    }
  });

  // Re-fit after all resources load and once web fonts are ready — the menu's
  // width depends on the Futura face, which may arrive after first paint.
  window.addEventListener('load', function() {
    patchSearchAppForDesktop();
    fitAllHeaders();
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitAllHeaders);
  }
})();
